import CardHeader from "components/CardHeader";
import { useAdminConsoleVisibility } from "components/AdminConsole/adminConsoleVisibility";
import MinorUnitInput from "components/MinorUnitInput";
import PageMeta from "components/PageMeta";
import PdfDocumentViewer from "components/PdfDocumentViewer";
import ProjectTimeline, {
    getProjectLifecycleStatus,
    getProjectProgress,
} from "components/ProjectTimeline";
import WaitingDots from "components/WaitingDots";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Account, AccountWorkerRates } from "models/Account";
import { Invoice } from "models/Invoice";
import { Project, ProjectAttachment, ProjectWorker } from "models/Project";
import React, { useEffect, useMemo, useState } from "react";
import { Circle, MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet";
import { Link, useParams } from "react-router-dom";
import {
    Alert, Badge, Button, Card, CardBody, Col, FormGroup, Input, Label, Modal, ModalBody, ModalFooter,
    ModalHeader, Row, Spinner
} from "reactstrap";
import * as api from "services/Api";
import ProjectHoursOverview from "./ProjectHoursOverview";
import ProjectInvoicesCard from "./ProjectInvoicesCard";
import ProjectBudgetProgress from "./ProjectBudgetProgress";
import "./project-detail.scss";
import { useOfficeTranslation } from "i18n/OfficeTranslation";

const inputDate = (value: string) => value.slice(0, 10);
const responsibleProjectInvoices = (project: Project, invoices: Invoice[]) =>
    invoices
        .filter(invoice =>
            invoice.project_id === project.id &&
            !!invoice.supplier &&
            !invoice.quote
        )
        .sort((a, b) => b.GetCreatedDate().getTime() - a.GetCreatedDate().getTime());
const defaultCenter: [number, number] = [52.1326, 5.2913];
interface PreviewProjectAttachment extends ProjectAttachment { preview_url?: string }
const pinIcon = L.divIcon({
    className: "",
    html: '<span class="project-map-pin"><i class="mdi mdi-map-marker"></i></span>',
    iconSize: [34, 42],
    iconAnchor: [17, 42]
});

const MapClickHandler: React.FC<{ onPick: (latitude: number, longitude: number) => void }> = ({ onPick }) => {
    useMapEvents({ click: (event: { latlng: { lat: number; lng: number } }) => onPick(event.latlng.lat, event.latlng.lng) });
    return null;
};

const MapLocationController: React.FC<{ location: [number, number] | null }> = ({ location }) => {
    const map = useMap();
    useEffect(() => {
        if (location) map.flyTo(location, Math.max(map.getZoom(), 14));
    }, [location, map]);
    return null;
};

const ProjectDetail: React.FC = () => {
    const { intlLocale, t } = useOfficeTranslation();
    const adminConsoleOpen = useAdminConsoleVisibility();
    const formatDate = (value: string) =>
        new Intl.DateTimeFormat(intlLocale, { dateStyle: "medium" }).format(new Date(value));
    const { id = "" } = useParams();
    const [project, setProject] = useState<Project | null>(null);
    const [draft, setDraft] = useState<Project | null>(null);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [documents, setDocuments] = useState<Record<string, ProjectAttachment[]>>({});
    const [projectInvoices, setProjectInvoices] = useState<Invoice[]>([]);
    const [selectedAccount, setSelectedAccount] = useState("");
    const [selectedWorkerRole, setSelectedWorkerRole] = useState("");
    const [previewDocument, setPreviewDocument] = useState<ProjectAttachment | null>(null);
    const [previewContent, setPreviewContent] = useState<PreviewProjectAttachment | null>(null);
    const [previewError, setPreviewError] = useState("");
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savingLocation, setSavingLocation] = useState(false);
    const [savingResponsible, setSavingResponsible] = useState(false);
    const [savingWorkerRates, setSavingWorkerRates] = useState(false);
    const [deletingWorkerId, setDeletingWorkerId] = useState("");
    const [addressQuery, setAddressQuery] = useState("");
    const [addressResults, setAddressResults] = useState<api.ProjectAddressSearchResult[]>([]);
    const [searchingAddress, setSearchingAddress] = useState(false);
    const [addressSearchError, setAddressSearchError] = useState("");
    const [rateWorkerId, setRateWorkerId] = useState("");
    const [rateDraft, setRateDraft] = useState<ProjectWorker | null>(null);
    const [message, setMessage] = useState<{ color: string; text: string } | null>(null);

    const loadDocuments = async (value: Project) => {
        const entries = await Promise.all(value.workers.map(async worker => [
            worker.account_id,
            await api.getProjectWorkerAttachments(value.id, worker.account_id)
        ] as const));
        setDocuments(Object.fromEntries(entries));
    };

    const load = async () => {
        try {
            const projectValue = await api.getProject(id);
            const [accountValues, invoiceValues] = await Promise.all([
                api.getAccounts(),
                api.getProjectInvoices(id),
            ]);
            setProject(projectValue);
            setDraft(projectValue);
            setAccounts(accountValues);
            setProjectInvoices(invoiceValues.filter(invoice => invoice.project_id === projectValue.id && !invoice.quote));
            await loadDocuments(projectValue);
        } catch {
            setMessage({ color: "danger", text: t("projects.projectDetail.projectCouldNotBeLoaded") });
        }
    };

    // load intentionally follows the current route id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [id]);

    useEffect(() => () => {
        if (previewContent?.preview_url) URL.revokeObjectURL(previewContent.preview_url);
    }, [previewContent]);

    const accountById = useMemo(() => new Map(accounts.map(account => [account.id, account])), [accounts]);
    const invoices = useMemo(
        () => project ? responsibleProjectInvoices(project, projectInvoices) : [],
        [project, projectInvoices],
    );
    const availableAccounts = accounts.filter(account => !project?.workers.some(worker => worker.account_id === account.id));
    const invoicedCosts = invoices.reduce((total, invoice) => total + invoice.CalcGrandTotal(), 0);

    const saveProject = async () => {
        if (!project || !draft) return;
        if (!draft.name.trim() || new Date(draft.end_date) < new Date(draft.start_date)) {
            setMessage({ color: "danger", text: t("projects.projectDetail.checkTheProjectNameAndProjectPeriod") });
            return;
        }
        setSaving(true);
        try {
            const updated = await api.updateProject({ ...draft, name: draft.name.trim() });
            setProject(updated);
            setDraft(updated);
            setEditing(false);
            setMessage({ color: "success", text: t("projects.projectDetail.projectDetailsSaved") });
        } catch (reason: any) {
            setMessage({ color: "danger", text: reason.response?.data?.error ?? t("projects.projectDetail.projectCouldNotBeSaved") });
        } finally {
            setSaving(false);
        }
    };

    const setLocation = (latitude: number, longitude: number) => {
        if (!draft) return;
        setDraft({ ...draft, latitude, longitude, location_radius: draft.location_radius || 1000 });
        setEditing(true);
    };

    const searchAddress = async (event: React.FormEvent) => {
        event.preventDefault();
        const query = addressQuery.trim();
        if (query.length < 3) return;
        setSearchingAddress(true);
        setAddressSearchError("");
        try {
            const results = await api.searchProjectAddresses(query, intlLocale);
            setAddressResults(results);
            if (results.length === 0) setAddressSearchError(t("projects.projectDetail.noAddressesFound"));
        } catch {
            setAddressResults([]);
            setAddressSearchError(t("projects.projectDetail.addressSearchFailed"));
        } finally {
            setSearchingAddress(false);
        }
    };

    const selectAddress = (result: api.ProjectAddressSearchResult) => {
        setAddressQuery(result.display_name);
        setAddressResults([]);
        setAddressSearchError("");
        setLocation(result.latitude, result.longitude);
    };

    const updateResponsible = async (accountId: string) => {
        if (!project) return;
        const currentProject = draft ?? project;
        setSavingResponsible(true);
        try {
            const updated = await api.updateProject({ ...currentProject, responsible: accountId ? [accountId] : [] });
            setProject(updated);
            setDraft(updated);
            setMessage({ color: "success", text: t("projects.projectDetail.responsibleAccountSaved") });
        } catch (reason: any) {
            setMessage({ color: "danger", text: reason.response?.data?.error ?? t("projects.projectDetail.responsibleAccountCouldNotBeSaved") });
        } finally {
            setSavingResponsible(false);
        }
    };

    const saveLocation = async () => {
        if (!project || !draft) return;
        setSavingLocation(true);
        try {
            const updated = await api.updateProject({
                ...project,
                latitude: draft.latitude,
                longitude: draft.longitude,
                location_radius: draft.location_radius
            });
            const expectedLocation = draft.latitude != null && draft.longitude != null;
            setProject(updated);
            setDraft(updated);
            setEditing(false);
            setMessage({ color: "success", text: t(expectedLocation ? "projects.location.saved" : "projects.location.cleared") });
        } catch (reason: any) {
            setMessage({ color: "danger", text: reason.response?.data?.error ?? reason.message ?? t("projects.projectDetail.projectLocationCouldNotBeSaved") });
        } finally {
            setSavingLocation(false);
        }
    };

    const addWorker = async () => {
        const role = selectedWorkerRole.trim();
        if (!project || !selectedAccount || !role) return;
        try {
            const updated = await api.linkProjectWorker(project.id, selectedAccount, { role });
            setProject(updated);
            setDraft(updated);
            setSelectedAccount("");
            setSelectedWorkerRole("");
            await loadDocuments(updated);
        } catch (reason: any) {
            setMessage({
                color: "danger",
                text: reason.response?.data?.error ?? t("projects.projectDetail.workerCouldNotBeAdded"),
            });
        }
    };

    const toggleWorkerRates = (worker: ProjectWorker) => {
        if (rateWorkerId === worker.account_id) {
            setRateWorkerId("");
            setRateDraft(null);
            return;
        }
        setRateWorkerId(worker.account_id);
        setRateDraft({ ...worker });
    };

    const saveWorkerRates = async () => {
        if (!project || !rateDraft) return;
        setSavingWorkerRates(true);
        try {
            const {
                entries: _entries,
                ...workerUpdate
            } = rateDraft;
            const updated = await api.linkProjectWorker(project.id, rateDraft.account_id, workerUpdate);
            setProject(updated);
            setDraft(updated);
            setRateWorkerId("");
            setRateDraft(null);
            setMessage({ color: "success", text: t("projects.projectDetail.workerProjectSettingsSaved") });
        } catch (reason: any) {
            setMessage({
                color: "danger",
                text: reason.response?.data?.error ?? t("projects.projectDetail.workerProjectSettingsCouldNotBeSaved"),
            });
        } finally {
            setSavingWorkerRates(false);
        }
    };

    const deleteWorker = async (worker: ProjectWorker, workerName: string) => {
        if (!project || !window.confirm(t("projects.projectDetail.removeWorkerProjectRelationConfirm", {
            name: workerName,
        }))) return;
        setDeletingWorkerId(worker.account_id);
        try {
            const updated = await api.deleteProjectWorker(project.id, worker.account_id);
            setProject(updated);
            setDraft(updated);
            setDocuments(current => {
                const next = { ...current };
                delete next[worker.account_id];
                return next;
            });
            if (rateWorkerId === worker.account_id) {
                setRateWorkerId("");
                setRateDraft(null);
            }
            setMessage({
                color: "success",
                text: t("projects.projectDetail.workerProjectRelationRemoved", {name: workerName}),
            });
        } catch (reason: any) {
            setMessage({
                color: "danger",
                text: reason.response?.data?.error ?? t("projects.projectDetail.workerProjectRelationCouldNotBeRemoved"),
            });
        } finally {
            setDeletingWorkerId("");
        }
    };

    const openDocument = async (accountId: string, document: ProjectAttachment) => {
        if (!project) return;
        setPreviewDocument(document);
        setPreviewContent(null);
        setPreviewError("");
        try {
            const complete = await api.getProjectWorkerAttachment(project.id, accountId, document.id);
            if (!complete.content && !complete.content_url) {
                setPreviewError(t("projects.projectDetail.documentCouldNotBeOpened"));
                return;
            }
            let previewUrl: string | undefined;
            if (!complete.content) {
                const content = await api.getProjectWorkerAttachmentContent(project.id, accountId, document.id);
                previewUrl = URL.createObjectURL(content);
            }
            setPreviewContent({...complete, preview_url: previewUrl});
        } catch {
            setPreviewError(t("projects.projectDetail.documentCouldNotBeOpened"));
        }
    };

    const closeDocument = () => {
        setPreviewDocument(null);
        setPreviewContent(null);
        setPreviewError("");
    };

    const deleteDocument = async (accountId: string, attachmentId: string) => {
        if (!project || !window.confirm(t("projects.projectDetail.deleteThisDocument"))) return;
        try {
            await api.deleteProjectWorkerAttachment(project.id, accountId, attachmentId);
            await loadDocuments(project);
        } catch {
            setMessage({ color: "danger", text: t("projects.projectDetail.documentCouldNotBeDeleted") });
        }
    };

    const refreshHours = async () => {
        try {
            const [updated, invoiceValues] = await Promise.all([
                api.getProject(id),
                api.getProjectInvoices(id),
            ]);
            setProject(updated);
            setProjectInvoices(invoiceValues.filter(invoice => invoice.project_id === updated.id && !invoice.quote));
            if (!editing) setDraft(updated);
        } catch {
            setMessage({ color: "danger", text: t("projects.projectDetail.theHoursOverviewCouldNotBeRefreshed") });
        }
    };

    if (!project || !draft) {
        return <div className="page-content">{message ? <Alert color={message.color}>{message.text}</Alert> : <WaitingDots />}</div>;
    }

    const progress = getProjectProgress(project);
    const lifecycleStatus = getProjectLifecycleStatus(project);
    const location: [number, number] | null =
        draft.latitude != null && draft.longitude != null ? [draft.latitude, draft.longitude] : null;

    return <div className="page-content">
        <PageMeta title={project.name} />

        <div className="page-title-box d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
                <Link to="/projects" className="text-muted d-inline-block mb-2">
                    <i className="mdi mdi-arrow-left me-1" />{t("projects.projectDetail.projects")}
                </Link>
                <h4 className="mb-1">{project.name}</h4>
                <p className="text-muted mb-0">
                    {lifecycleStatus === "completed"
                        ? t("projects.projectDetail.projectCompleted")
                        : lifecycleStatus === "upcoming"
                            ? t("projects.projectDetail.projectHasNotStartedYet")
                            : t("projects.projectDetail.percentagePercentageOfTheDurationElapsed", {percentage: Math.round(progress)})}
                </p>
            </div>
            <Badge
                color={lifecycleStatus === "completed" ? "success" : lifecycleStatus === "upcoming" ? "secondary" : "primary"}
                pill
                className="px-3 py-2"
            >
                {t(lifecycleStatus === "completed" ? "common.status.completed" : lifecycleStatus === "upcoming" ? "common.status.upcoming" : "common.status.active")}
            </Badge>
        </div>

        <Card className="mb-4">
            <CardBody>
                <CardHeader
                    title={t("projects.projectDetail.projectPlanning")}
                    icon="mdi-calendar-range-outline"
                    actions={<span className="text-muted">{Math.round(progress)}%</span>}
                />
                <div className="pt-4">
                    <ProjectTimeline project={project} />
                    <div className="d-flex justify-content-between mt-3 small">
                        <div>
                            <span className="text-muted d-block">{t("projects.projectDetail.start")}</span>
                            <strong>{formatDate(project.start_date)}</strong>
                        </div>
                        <div className="text-end">
                            <span className="text-muted d-block">{t("projects.projectDetail.end")}</span>
                            <strong>{formatDate(project.end_date)}</strong>
                        </div>
                    </div>
                </div>
            </CardBody>
        </Card>

        {message && <Alert color={message.color} toggle={() => setMessage(null)}>{message.text}</Alert>}

        <Row className="align-items-stretch">
            <Col xl={3} lg={5}>
                <Card className="project-column-card"><CardBody>
                    <CardHeader
                        title={t("projects.projectDetail.projectDetails")}
                        icon="mdi-briefcase-outline"
                        actions={!editing && <Button color="light" size="sm" onClick={() => setEditing(true)}>
                            <i className="mdi mdi-pencil-outline me-1" />{t("projects.projectDetail.edit")}
                        </Button>}
                    />
                    {editing ? <div className="pt-3">
                        <FormGroup><Label for="detail-name">{t("common.name")}</Label>
                            <Input id="detail-name" value={draft.name} onChange={event => setDraft({ ...draft, name: event.target.value })} />
                        </FormGroup>
                        <FormGroup><Label for="detail-start">{t("projects.projectDetail.startDate")}</Label>
                            <Input id="detail-start" type="date" value={inputDate(draft.start_date)}
                                onChange={event => setDraft({ ...draft, start_date: new Date(`${event.target.value}T00:00:00`).toISOString() })} />
                        </FormGroup>
                        <FormGroup><Label for="detail-end">{t("projects.projectDetail.endDate")}</Label>
                            <Input id="detail-end" type="date" min={inputDate(draft.start_date)} value={inputDate(draft.end_date)}
                                onChange={event => setDraft({ ...draft, end_date: new Date(`${event.target.value}T23:59:59`).toISOString() })} />
                        </FormGroup>
                        <FormGroup><Label for="detail-budget">{t("projects.projectDetail.budgetEuro")}</Label>
                            <MinorUnitInput id="detail-budget" min={0} locale={intlLocale} value={draft.budget}
                                onValueChange={value => setDraft({ ...draft, budget: value })} />
                        </FormGroup>
                        <div className="d-flex gap-2 pt-2">
                            <Button color="primary" className="flex-grow-1" disabled={saving} onClick={saveProject}>
                                {saving ? <Spinner size="sm" /> : t("common.save")}
                            </Button>
                            <Button color="light" onClick={() => { setDraft(project); setEditing(false); }}>{t("projects.projectDetail.cancel")}</Button>
                        </div>
                    </div> : <div>
                        <div className="d-flex justify-content-between gap-3 border-bottom py-2">
                            <span className="text-muted">{t("projects.projectDetail.startDate")}</span>
                            <span className="text-end">{formatDate(project.start_date)}</span>
                        </div>
                        <div className="d-flex justify-content-between gap-3 border-bottom py-2">
                            <span className="text-muted">{t("projects.projectDetail.endDate")}</span>
                            <span className="text-end">{formatDate(project.end_date)}</span>
                        </div>
                        <ProjectBudgetProgress
                            budget={project.budget / 100}
                            currency={project.currency}
                            invoicedCosts={invoicedCosts}
                        />
                    </div>}
                </CardBody></Card>

                <Card><CardBody>
                    <CardHeader
                        title={t("projects.projectDetail.projectLocation")}
                        icon="mdi-map-marker-outline"
                        actions={location && <Button size="sm" color="light" onClick={() => { setDraft({ ...draft, latitude: null, longitude: null, location_radius: null }); setEditing(true); }}>
                            {t("projects.projectDetail.clear")}
                        </Button>}
                    />
                    <form className="mt-3 mb-2" onSubmit={searchAddress}>
                        <Label for="project-address-search">{t("projects.projectDetail.searchAddress")}</Label>
                        <div className="input-group">
                            <Input
                                id="project-address-search"
                                value={addressQuery}
                                placeholder={t("projects.projectDetail.addressSearchPlaceholder")}
                                autoComplete="street-address"
                                onChange={event => {
                                    setAddressQuery(event.target.value);
                                    setAddressResults([]);
                                    setAddressSearchError("");
                                }}
                            />
                            <Button color="light" type="submit" disabled={searchingAddress || addressQuery.trim().length < 3}>
                                {searchingAddress ? <Spinner size="sm" /> : <><i className="mdi mdi-magnify me-1" />{t("common.search")}</>}
                            </Button>
                        </div>
                    </form>
                    {addressSearchError && <small className="text-danger d-block mb-2">{addressSearchError}</small>}
                    {addressResults.length > 0 && <div className="list-group mb-3 project-address-results">
                        {addressResults.map(result => <button
                            className="list-group-item list-group-item-action small text-start"
                            key={`${result.latitude}-${result.longitude}-${result.display_name}`}
                            type="button"
                            onClick={() => selectAddress(result)}
                        >
                            <i className="mdi mdi-map-marker-outline me-2 text-primary" />
                            {result.display_name}
                        </button>)}
                    </div>}
                    <p className="text-muted mb-3">{t("projects.projectDetail.clickTheMapOrDragThePin")}</p>
                    <div className="project-map mb-3">
                        <MapContainer center={location ?? defaultCenter} zoom={location ? 14 : 7} scrollWheelZoom>
                            <TileLayer attribution={t("projects.projectDetail.mapAttribution")} url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                            <MapLocationController location={location} />
                            <MapClickHandler onPick={setLocation} />
                            {location && <>
                                <Circle center={location} radius={draft.location_radius || 1000}
                                        pathOptions={{ color: "#556ee6", fillColor: "#556ee6", fillOpacity: .14 }} />
                                <Marker position={location} icon={pinIcon} draggable eventHandlers={{
                                    dragend: event => {
                                        const point = event.target.getLatLng();
                                        setLocation(point.lat, point.lng);
                                    }
                                }} />
                            </>}
                        </MapContainer>
                    </div>
                    <Label for="location-radius">{t("projects.projectDetail.radiusAroundThePin")}</Label>
                    <Input id="location-radius" type="select" value={draft.location_radius || 1000} disabled={!location}
                           onChange={event => { setDraft({ ...draft, location_radius: Number(event.target.value) }); setEditing(true); }}>
                        <option value={100}>{t("projects.projectDetail.distanceMeters", {distance: 100})}</option>
                        <option value={250}>{t("projects.projectDetail.distanceMeters", {distance: 250})}</option>
                        <option value={500}>{t("projects.projectDetail.distanceMeters", {distance: 500})}</option>
                        <option value={1000}>{t("projects.projectDetail.distanceKilometers", {distance: 1})}</option>
                        <option value={2500}>{t("projects.projectDetail.distanceKilometers", {distance: new Intl.NumberFormat(intlLocale).format(2.5)})}</option>
                        <option value={5000}>{t("projects.projectDetail.distanceKilometers", {distance: 5})}</option>
                        <option value={10000}>{t("projects.projectDetail.distanceKilometers", {distance: 10})}</option>
                    </Input>
                    {location && <small className="text-muted d-block mt-2">{location[0].toFixed(5)}, {location[1].toFixed(5)}</small>}
                    <Button color="primary" className="w-100 mt-3" disabled={savingLocation || (!location && project.latitude == null)}
                            onClick={saveLocation}>
                        {savingLocation ? <><Spinner size="sm" className="me-2" />{t("common.saving")}</> :
                            <><i className="mdi mdi-map-marker-check-outline me-1" />{t("projects.projectDetail.saveLocation")}</>}
                    </Button>
                </CardBody></Card>
            </Col>

            <Col xl={5} lg={7}>
                <Card className="project-column-card"><CardBody>
                    <CardHeader
                        title={t("projects.projectDetail.workers")}
                        icon="mdi-account-multiple-outline"
                        count={project.workers.length}
                    />
                    {project.workers.map(worker => {
                        const account = accountById.get(worker.account_id);
                        const attachments = documents[worker.account_id] ?? [];
                        const accountRates: AccountWorkerRates = {
                            ...new Account().worker_rates,
                            ...(account?.worker_rates ?? {}),
                        };
                        const editingRates = rateWorkerId === worker.account_id && rateDraft;
                        return <div className="border-bottom py-3" key={worker.account_id}>
                            <div className="d-flex justify-content-between align-items-center gap-2">
                                <div>
                                    <Link to={`/accounts/${worker.account_id}`} className="text-body">
                                        <strong>{account?.legal_name ?? worker.account_id}</strong>
                                    </Link>
                                    {worker.role?.trim() ?
                                        <span className="badge bg-primary bg-soft text-primary ms-2 fw-normal">
                                            {worker.role.trim()}
                                        </span> :
                                        <span className="badge bg-warning bg-soft text-warning ms-2 fw-normal">
                                            <i className="mdi mdi-alert-circle-outline me-1" aria-hidden="true" />
                                            {t("projects.projectDetail.noWorkerRoleAssigned")}
                                        </span>
                                    }
                                </div>
                                <div className="d-flex gap-1">
                                    {adminConsoleOpen && <Button
                                        aria-label={t("projects.projectDetail.removeWorkerProjectRelationFor", {name: account?.legal_name ?? worker.account_id})}
                                        color="danger"
                                        outline
                                        size="sm"
                                        title={t("projects.projectDetail.removeWorkerProjectRelation")}
                                        disabled={deletingWorkerId === worker.account_id}
                                        onClick={() => deleteWorker(worker, account?.legal_name ?? worker.account_id)}
                                    >
                                        {deletingWorkerId === worker.account_id
                                            ? <Spinner size="sm" />
                                            : <i className="mdi mdi-link-variant-off" />}
                                    </Button>}
                                    <Button
                                        aria-expanded={!!editingRates}
                                        aria-label={t("projects.projectDetail.workerSettingsFor", {name: account?.legal_name ?? worker.account_id})}
                                        color="light"
                                        size="sm"
                                        title={t("projects.projectDetail.workerSettingsTitle")}
                                        onClick={() => toggleWorkerRates(worker)}
                                    >
                                        <i className="mdi mdi-cog-outline" />
                                    </Button>
                                </div>
                            </div>
                            <div className="mt-2">
                                {attachments.length === 0 && <p className="text-muted small mb-2">{t("projects.projectDetail.noContractsOrAgreementsYet")}</p>}
                                {attachments.map(document => <div className="d-flex align-items-center justify-content-between gap-2 border-bottom py-2" key={document.id}>
                                    <div className="text-truncate"><i className="fas fa-file-pdf text-danger me-2" />{document.name}</div>
                                    <div className="d-flex gap-1">
                                        {document.kind === "contract" && <Badge color={document.signature_status === "signed" ? "success" : "warning"}>
                                            {t(document.signature_status === "signed" ? "common.status.signed" : "common.status.open")}
                                        </Badge>}
                                        <Button size="sm" color="light" onClick={() => openDocument(worker.account_id, document)}><i className="mdi mdi-eye-outline" /></Button>
                                        <Button size="sm" color="light" onClick={() => deleteDocument(worker.account_id, document.id)}><i className="mdi mdi-delete-outline text-danger" /></Button>
                                    </div>
                                </div>)}
                            </div>
                            {editingRates && <div className="border rounded p-3 mt-3">
                                <FormGroup className="mb-3">
                                    <Label for={`worker-role-${worker.account_id}`}><strong>{t("projects.projectDetail.workerRole")}</strong></Label>
                                    <Input
                                        id={`worker-role-${worker.account_id}`}
                                        type="text"
                                        value={rateDraft.role ?? ""}
                                        placeholder={t("projects.projectDetail.forExampleEngineerOrProjectLead")}
                                        onChange={event => setRateDraft(current => current ? {
                                            ...current,
                                            role: event.target.value,
                                        } : current)}
                                    />
                                    <small className="text-muted">
                                        {t("projects.projectDetail.projectSpecificRoleAvailableInContractTemplatesAsField", {field: "{{worker-role}}"})}
                                    </small>
                                </FormGroup>
                                <div className="mb-3">
                                    <strong>{t("projects.projectDetail.customRates")}</strong>
                                    <small className="text-muted d-block mt-1">
                                        {t("projects.projectDetail.visibleInternallyOnlyWithoutAnOverrideTheAccountRatesApply")}
                                    </small>
                                    <small className="text-muted d-block mt-1">
                                        {t("projects.projectDetail.accountDefaultHourlyRate", {rate: new Intl.NumberFormat(intlLocale, {
                                            style: "currency",
                                            currency: account?.billing?.currency || project.currency,
                                        }).format(accountRates.hourly_rate / 100)})} {" · "}
                                        {accountRates.platform_fee_type === "percentage"
                                            ? t("projects.projectDetail.percentagePlatformFee", {percentage: (accountRates.platform_fee_value / 100).toFixed(2)})
                                            : t("projects.projectDetail.fixedPlatformFee", {fee: new Intl.NumberFormat(intlLocale, {
                                                style: "currency",
                                                currency: account?.billing?.currency || project.currency,
                                            }).format(accountRates.platform_fee_value / 100)})}
                                    </small>
                                </div>

                                <div className="form-check mb-2">
                                    <Input
                                        id={`hourly-rate-override-${worker.account_id}`}
                                        type="checkbox"
                                        checked={rateDraft.hourly_rate_override !== undefined}
                                        onChange={event => setRateDraft(current => current ? {
                                            ...current,
                                            hourly_rate_override: event.target.checked
                                                ? accountRates.hourly_rate
                                                : undefined,
                                        } : current)}
                                    />
                                    <label className="form-check-label" htmlFor={`hourly-rate-override-${worker.account_id}`}>
                                        {t("projects.projectDetail.customHourlyRate")}
                                    </label>
                                </div>
                                {rateDraft.hourly_rate_override !== undefined &&
                                    <div className="input-group mb-3">
                                        <span className="input-group-text">€</span>
                                        <MinorUnitInput
                                            aria-label={t("projects.projectDetail.customHourlyRate")}
                                            min={0}
                                            locale={intlLocale}
                                            value={rateDraft.hourly_rate_override}
                                            onValueChange={value => setRateDraft(current => current ? {
                                                ...current,
                                                hourly_rate_override: value,
                                            } : current)}
                                        />
                                        <span className="input-group-text">{t("projects.projectDetail.perHour")}</span>
                                    </div>
                                }

                                <div className="form-check mb-2">
                                    <Input
                                        id={`platform-fee-override-${worker.account_id}`}
                                        type="checkbox"
                                        checked={rateDraft.platform_fee_value_override !== undefined}
                                        onChange={event => setRateDraft(current => current ? {
                                            ...current,
                                            platform_fee_type_override: event.target.checked
                                                ? accountRates.platform_fee_type
                                                : undefined,
                                            platform_fee_value_override: event.target.checked
                                                ? accountRates.platform_fee_value
                                                : undefined,
                                        } : current)}
                                    />
                                    <label className="form-check-label" htmlFor={`platform-fee-override-${worker.account_id}`}>
                                        {t("projects.projectDetail.customPlatformFee")}
                                    </label>
                                </div>
                                {rateDraft.platform_fee_value_override !== undefined &&
                                    rateDraft.platform_fee_type_override !== undefined && <>
                                        <Input
                                            aria-label={t("projects.projectDetail.customPlatformFeeType")}
                                            className="mb-2"
                                            type="select"
                                            value={rateDraft.platform_fee_type_override}
                                            onChange={event => setRateDraft(current => current ? {
                                                ...current,
                                                platform_fee_type_override: event.target.value as "percentage" | "fixed",
                                            } : current)}
                                        >
                                            <option value="percentage">{t("projects.projectDetail.percentageOfHourlyRate")}</option>
                                            <option value="fixed">{t("projects.projectDetail.fixedAmountPerHour")}</option>
                                        </Input>
                                        <div className="input-group mb-3">
                                            <span className="input-group-text">
                                                {rateDraft.platform_fee_type_override === "percentage" ? "%" : "€"}
                                            </span>
                                            <MinorUnitInput
                                                aria-label={t("projects.projectDetail.customPlatformFee")}
                                                min={0}
                                                max={rateDraft.platform_fee_type_override === "percentage" ? 100 : undefined}
                                                locale={intlLocale}
                                                value={rateDraft.platform_fee_value_override}
                                                onValueChange={value => setRateDraft(current => current ? {
                                                    ...current,
                                                    platform_fee_value_override: value,
                                                } : current)}
                                            />
                                            {rateDraft.platform_fee_type_override === "fixed" &&
                                                <span className="input-group-text">{t("projects.projectDetail.perHour")}</span>
                                            }
                                        </div>
                                    </>
                                }

                                <div className="d-flex justify-content-end gap-2">
                                    <Button
                                        color="light"
                                        size="sm"
                                        disabled={savingWorkerRates}
                                        onClick={() => {
                                            setRateWorkerId("");
                                            setRateDraft(null);
                                        }}
                                    >
                                        {t("common.cancel")}
                                    </Button>
                                    <Button
                                        color="primary"
                                        size="sm"
                                        disabled={savingWorkerRates}
                                        onClick={saveWorkerRates}
                                    >
                                        {savingWorkerRates ? <Spinner size="sm" /> : t("common.save")}
                                    </Button>
                                </div>
                            </div>}
                        </div>;
                    })}
                    {project.workers.length === 0 &&
                        <p className="text-muted mt-3 mb-0">{t("projects.projectDetail.noWorkersLinkedYet")}</p>
                    }
                </CardBody></Card>
            </Col>

            <Col xl={4}>
                <Card><CardBody>
                    <CardHeader
                        title={t("projects.projectDetail.responsibleAccount")}
                        icon="mdi-account-star-outline"
                    />
                    <p className="text-muted mt-3 mb-3">{t("projects.projectDetail.ultimatelyResponsibleForThisProject")}</p>
                    <Input aria-label={t("projects.projectDetail.responsibleAccount")} type="select" value={project.responsible[0] ?? ""} disabled={savingResponsible}
                        onChange={event => updateResponsible(event.target.value)}>
                        <option value="">{t("projects.projectDetail.notAssignedYet")}</option>
                        {accounts.map(account => <option key={account.id} value={account.id}>{account.legal_name}</option>)}
                    </Input>
                    {savingResponsible && <small className="text-muted d-block mt-2"><Spinner size="sm" className="me-1" />{t("common.saving")}</small>}
                </CardBody></Card>
                <Card><CardBody>
                    <CardHeader
                        title={t("projects.projectDetail.addWorker")}
                        icon="mdi-account-plus-outline"
                    />
                    <Input className="mt-3" aria-label={t("projects.projectDetail.addWorker")} type="select" value={selectedAccount} onChange={event => setSelectedAccount(event.target.value)}>
                        <option value="">{t("projects.projectDetail.chooseAnAccount")}</option>
                        {availableAccounts.map(account => <option key={account.id} value={account.id}>{account.legal_name}</option>)}
                    </Input>
                    <FormGroup className="mt-3 mb-0">
                        <Label for="new-worker-role"><strong>{t("projects.projectDetail.workerRole")}</strong></Label>
                        <Input
                            id="new-worker-role"
                            type="text"
                            required
                            value={selectedWorkerRole}
                            placeholder={t("projects.projectDetail.forExampleEngineerOrProjectLead")}
                            onChange={event => setSelectedWorkerRole(event.target.value)}
                        />
                        <small className="text-muted">
                            {t("projects.projectDetail.workerRoleRequiredForContracts")}
                        </small>
                    </FormGroup>
                    <Button color="primary" className="w-100 mt-3" disabled={!selectedAccount || !selectedWorkerRole.trim()} onClick={addWorker}>{t("projects.projectDetail.addToProject")}</Button>
                </CardBody></Card>
                <ProjectInvoicesCard invoices={invoices} accountById={accountById} currency={project.currency} />
            </Col>
        </Row>
        <ProjectHoursOverview project={project} accountById={accountById} onRefresh={refreshHours} />
        <Modal isOpen={previewDocument !== null} toggle={closeDocument} size="xl" scrollable>
            <ModalHeader toggle={closeDocument}>{previewDocument?.name}</ModalHeader>
            <ModalBody>
                {!previewContent && !previewError && <div className="text-center py-5"><Spinner /></div>}
                {previewError && <Alert color="danger">{previewError}</Alert>}
                {previewContent && <PdfDocumentViewer
                    content={previewContent.content}
                    url={previewContent.preview_url}
                    contentType={previewContent.content_type}
                    loading={<div className="text-center py-5"><Spinner /></div>}
                    error={<Alert color="danger">{t("projects.projectDetail.documentCouldNotBeOpened")}</Alert>}
                />}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={closeDocument}>{t("common.close")}</Button>
            </ModalFooter>
        </Modal>
    </div>;
};

export default ProjectDetail;
